import * as React from 'react'
import { useNavigate, useParams } from 'react-router'
import { Loader2, Sprout } from 'lucide-react'
import { acceptInviteInputSchema } from '@lib/input/invite.input'
import { parseLinkToken } from '@lib/validators/link-token.validator'
import { PASSWORD_MIN_LENGTH } from '@lib/validators/password.validator'
import { useAuth } from '@/auth/AuthContext'
import { useInvitePreview } from '@/hooks/useInvitePreview'
import { useAcceptInvite } from '@/hooks/useAcceptInvite'
import { isApiError } from '@/helpers/apiClient'
import { toErrorMessage } from '@/helpers/logger'
import { Button } from '@/components/ui/button'
import { Card, CardContent } from '@/components/ui/card'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { useOrganizationBranding } from '@/hooks/useOrganization'

/** Field-level errors keyed by the form field they belong to. */
interface FieldErrors {
  name?: string
  password?: string
  confirmPassword?: string
}

/** Turn an accept failure into a message that explains the recovery path. */
function acceptErrorMessage<E>(error: E): string {
  if (isApiError(error)) {
    if (error.status === 410) {
      return 'This invitation is no longer valid. Ask for a new one.'
    }
    if (error.status === 409) {
      return (
        error.body.message ??
        'An account already exists for this email. Try signing in instead.'
      )
    }
    return error.body.message ?? 'Could not create your account. Try again.'
  }
  return toErrorMessage(error)
}

/**
 * The public accept-invite page — the one route that creates an account. It is
 * reached from an emailed link carrying the invite token, and is the only place
 * outside `/login` that an unauthenticated visitor can use.
 *
 * The token is validated before the form is shown, so a dead link says so
 * immediately rather than after the visitor has typed a password. A damaged
 * one says so without asking the API at all.
 */
export function AcceptInvitePage() {
  const params = useParams<{ token: string }>()
  const token = parseLinkToken(params.token)
  if (token === null) {
    return (
      <AcceptShell>
        <InvitationExpired />
      </AcceptShell>
    )
  }
  return <AcceptInvite token={token} />
}

function AcceptInvite({ token }: { token: string }) {
  const navigate = useNavigate()
  const { adoptSession } = useAuth()

  const preview = useInvitePreview(token)
  const acceptInvite = useAcceptInvite(token)

  const [name, setName] = React.useState<string>('')
  const [password, setPassword] = React.useState<string>('')
  const [confirmPassword, setConfirmPassword] = React.useState<string>('')
  const [fieldErrors, setFieldErrors] = React.useState<FieldErrors>({})
  const [formError, setFormError] = React.useState<string | null>(null)

  async function handleSubmit(
    event: React.FormEvent<HTMLFormElement>,
  ): Promise<void> {
    event.preventDefault()
    setFieldErrors({})
    setFormError(null)

    // Confirmation is a client-only concern — the API takes a single password —
    // so it is checked here rather than in the shared schema.
    if (password !== confirmPassword) {
      setFieldErrors({ confirmPassword: 'Passwords do not match' })
      return
    }

    const parsed = acceptInviteInputSchema.safeParse({ name, password })
    if (!parsed.success) {
      const next: FieldErrors = {}
      for (const issue of parsed.error.issues) {
        const field = issue.path[0]
        if (field === 'name' && !next.name) next.name = issue.message
        if (field === 'password' && !next.password) next.password = issue.message
      }
      setFieldErrors(next)
      return
    }

    try {
      const session = await acceptInvite.mutateAsync(parsed.data)
      adoptSession(session)
      navigate('/', { replace: true })
    } catch (caught) {
      setFormError(acceptErrorMessage(caught))
    }
  }

  if (preview.isPending) {
    return (
      <AcceptShell>
        <div className="flex items-center justify-center py-8">
          <Loader2 className="h-5 w-5 animate-spin text-muted-text" />
        </div>
      </AcceptShell>
    )
  }

  if (preview.isError) {
    return (
      <AcceptShell>
        <InvitationExpired />
      </AcceptShell>
    )
  }

  return (
    <AcceptShell>
      <h1 className="text-lg font-semibold">Finish setting up your account</h1>
      <p className="mt-1 text-sm text-secondary-text">
        You were invited as{' '}
        <span className="font-medium text-primary-text">
          {preview.data?.email}
        </span>
        .
      </p>

      <form className="mt-6 space-y-4" onSubmit={handleSubmit} noValidate>
        <div className="space-y-1.5">
          <Label htmlFor="name">Full name</Label>
          <Input
            id="name"
            autoComplete="name"
            placeholder="Sam Grower"
            value={name}
            onChange={(event) => setName(event.target.value)}
          />
          {fieldErrors.name ? (
            <p className="text-xs text-destructive">{fieldErrors.name}</p>
          ) : null}
        </div>

        <div className="space-y-1.5">
          <Label htmlFor="password">Password</Label>
          <Input
            id="password"
            type="password"
            autoComplete="new-password"
            value={password}
            onChange={(event) => setPassword(event.target.value)}
          />
          {fieldErrors.password ? (
            <p className="text-xs text-destructive">{fieldErrors.password}</p>
          ) : (
            <p className="text-xs text-muted-text">
              At least {PASSWORD_MIN_LENGTH} characters. A passphrase is easier
              to remember and harder to guess.
            </p>
          )}
        </div>

        <div className="space-y-1.5">
          <Label htmlFor="confirm-password">Confirm password</Label>
          <Input
            id="confirm-password"
            type="password"
            autoComplete="new-password"
            value={confirmPassword}
            onChange={(event) => setConfirmPassword(event.target.value)}
          />
          {fieldErrors.confirmPassword ? (
            <p className="text-xs text-destructive">
              {fieldErrors.confirmPassword}
            </p>
          ) : null}
        </div>

        {formError ? (
          <p className="text-sm text-destructive">{formError}</p>
        ) : null}

        <Button
          type="submit"
          className="w-full"
          disabled={acceptInvite.isPending}
        >
          {acceptInvite.isPending ? (
            <Loader2 className="h-4 w-4 animate-spin" />
          ) : null}
          {acceptInvite.isPending ? 'Creating account…' : 'Create account'}
        </Button>
      </form>
    </AcceptShell>
  )
}

/** The one dead end for a link that does not open, whatever the reason. */
function InvitationExpired() {
  const navigate = useNavigate()
  return (
    <>
      <h1 className="text-lg font-semibold">This invitation has expired</h1>
      <p className="mt-2 text-sm text-secondary-text">
        Invitation links are single-use and time-limited. Ask whoever invited
        you to send a new one.
      </p>
      <Button
        variant="outline"
        className="mt-6 w-full"
        onClick={() => navigate('/login')}
      >
        Go to sign in
      </Button>
    </>
  )
}

interface AcceptShellProps {
  children: React.ReactNode
}

/** Shared centered card for every state of this page, so it never reflows. */
function AcceptShell({ children }: AcceptShellProps) {
  const branding = useOrganizationBranding()
  const organizationName = branding.data?.name ?? ''
  return (
    <div className="flex min-h-svh items-center justify-center bg-app p-4">
      <div className="w-full max-w-sm">
        <div className="mb-6 flex items-center gap-2">
          <Sprout className="h-5 w-5 text-brand" />
          <span className="font-semibold">{organizationName}</span>
        </div>
        <Card>
          <CardContent className="p-6">{children}</CardContent>
        </Card>
      </div>
    </div>
  )
}
